import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Hotel, Building2, ClipboardList, Users, BedDouble, Moon, Search,
  ChevronDown, ChevronRight, FileSpreadsheet, FileText, Printer,
  SlidersHorizontal, Filter, Loader2, MapPin,
} from "lucide-react";
import styles from "./HotelReport.module.css";
import { useToast } from "../../../context/ToastContext";
import {
  getHotelManifest, getHotelFilterOptions, downloadHotelReportPdf,
} from "../../../api/reports/useReportsApi";
import { formatReportDate, downloadExcel, printHtml, buildFilterLines, filtersToPrintHtml } from "../reportUtils";

const EMPTY_SUMMARY = { hotels: 0, bookings: 0, guests: 0, rooms: 0, nights: 0 };
const BOOKING_STATUSES = ["Confirmed", "Cancelled", "Completed"];
const PAGE_SIZES = [25, 50, 100];
const TYPE_FULL = { ADT: "Adult", CHD: "Child", INF: "Infant" };

const KPI_META = [
  { key: "hotels", label: "Hotels", Icon: Building2, cls: "kpiBlue" },
  { key: "bookings", label: "Bookings", Icon: ClipboardList, cls: "kpiGreen" },
  { key: "guests", label: "Guests", Icon: Users, cls: "kpiPurple" },
  { key: "rooms", label: "Rooms", Icon: BedDouble, cls: "kpiOrange" },
  { key: "nights", label: "Nights", Icon: Moon, cls: "kpiCyan" },
];

const TypeBadge = ({ type }) => (
  <span className={`${styles.typeBadge} ${styles[`type${type}`] || styles.typeADT}`}>
    {TYPE_FULL[type] || type}
  </span>
);

const HotelGroup = ({ hotel, open, onToggle }) => {
  const initials = String(hotel.hotelName || "?").trim().slice(0, 2).toUpperCase();
  const loc = [hotel.city, hotel.country].filter(Boolean).join(", ");
  return (
    <div className={`${styles.hotelRow} ${open ? styles.hotelRowOpen : ""}`}>
      <button className={styles.hotelHead} onClick={onToggle} type="button">
        <div className={styles.hMark}><Hotel size={18} /><span>{initials}</span></div>
        <div className={styles.hInfo}>
          <span className={styles.hName}>{hotel.hotelName}</span>
          <span className={styles.hLoc}>
            {loc && <><MapPin size={11} /> {loc}</>}
            {hotel.supplier ? <span className={styles.hSupplier}>· {hotel.supplier}</span> : null}
          </span>
        </div>
        <div className={styles.hCol}><span className={styles.hColLabel}>Bookings</span><span className={styles.hColValue}>{hotel.bookings}</span></div>
        <div className={styles.hCol}><span className={styles.hColLabel}>Guests</span><span className={styles.hColValue}>{hotel.guests}</span></div>
        <div className={styles.hCol}><span className={styles.hColLabel}>Rooms</span><span className={styles.hColValue}>{hotel.rooms}</span></div>
        <span className={styles.hChevron}>{open ? <ChevronDown size={17} /> : <ChevronRight size={17} />}</span>
      </button>

      {open && (
        <div className={styles.paxWrap}>
          <table className={styles.paxTable}>
            <thead>
              <tr>
                <th className={styles.pNo}>#</th>
                <th>Booking No</th>
                <th>Guest Name</th>
                <th>Type</th>
                <th>Check-in</th>
                <th>Check-out</th>
                <th>Room Type</th>
                <th>Characteristic</th>
                <th>Board</th>
                <th>Supplier Ref</th>
              </tr>
            </thead>
            <tbody>
              {hotel.roomingList.map((p, i) => (
                <tr key={`${p.bookingReference}-${i}`}>
                  <td className={styles.pNo}>{i + 1}</td>
                  <td className={styles.pRef}>{p.bookingReference}</td>
                  <td className={styles.pName}>{p.guestName}</td>
                  <td><TypeBadge type={p.travelerType} /></td>
                  <td>{formatReportDate(p.checkIn)}</td>
                  <td>{formatReportDate(p.checkOut)}</td>
                  <td>{p.roomType || "—"}</td>
                  <td>{p.characteristic || "—"}</td>
                  <td>{p.board || "—"}</td>
                  <td className={styles.pRef}>{p.supplierReference || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const HotelReport = () => {
  const { showToast } = useToast();

  const [draft, setDraft] = useState({
    dateFrom: "", dateTo: "", hotel: "", country: "", destination: "",
    roomType: "", characteristic: "", supplier: "", bookingStatus: "Confirmed",
  });
  const [applied, setApplied] = useState(null);
  const EMPTY_OPTIONS = { hotels: [], countries: [], suppliers: [], roomTypes: [], characteristics: [], destinationsByCountry: {} };
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [hotels, setHotels] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [expanded, setExpanded] = useState(() => new Set());

  const setField = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }));

  const runReport = useCallback(async (filters, { announce = false } = {}) => {
    setLoading(true);
    setError("");
    try {
      const res = await getHotelManifest(filters);
      if (!res?.success) throw new Error(res?.message || "Failed to generate hotel report");
      const list = Array.isArray(res.data?.hotels) ? res.data.hotels : [];
      setHotels(list);
      setSummary(res.data?.summary || EMPTY_SUMMARY);
      setApplied(filters);
      setPage(1);
      setExpanded(new Set(list.length ? [list[0].key] : []));
      if (announce) showToast(`Report generated — ${list.length} hotel${list.length === 1 ? "" : "s"}`, "success");
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to generate hotel report";
      setError(msg);
      setHotels([]);
      setSummary(EMPTY_SUMMARY);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const bootRef = useRef(false);
  useEffect(() => {
    if (bootRef.current) return;
    bootRef.current = true;
    getHotelFilterOptions()
      .then((res) => { if (res?.success) setOptions({ ...EMPTY_OPTIONS, ...(res.data || {}) }); })
      .catch(() => {});
    runReport({ ...draft });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerate = () => runReport({ ...draft }, { announce: true });

  const toggleExpand = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return hotels;
    return hotels.filter((h) => {
      if (
        String(h.hotelName).toLowerCase().includes(q) ||
        String(h.city).toLowerCase().includes(q) ||
        String(h.country).toLowerCase().includes(q) ||
        String(h.supplier).toLowerCase().includes(q)
      ) return true;
      return h.roomingList.some(
        (p) =>
          String(p.guestName).toLowerCase().includes(q) ||
          String(p.bookingReference).toLowerCase().includes(q) ||
          String(p.supplierReference).toLowerCase().includes(q) ||
          String(p.roomType).toLowerCase().includes(q) ||
          String(p.characteristic).toLowerCase().includes(q)
      );
    });
  }, [hotels, search]);

  const total = filtered.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pageCount);
  const start = (safePage - 1) * pageSize;
  const paged = filtered.slice(start, start + pageSize);
  const rangeFrom = total === 0 ? 0 : start + 1;
  const rangeTo = Math.min(start + pageSize, total);

  const EXPORT_HEADERS = [
    "Hotel", "Country", "City", "Supplier", "Booking No", "Guest Name",
    "Traveller Type", "Check-in", "Check-out", "Room Type", "Characteristic", "Board", "Supplier Ref",
  ];
  const exportRows = () =>
    filtered.flatMap((h) =>
      h.roomingList.map((p) => [
        h.hotelName, h.country, h.city, h.supplier, p.bookingReference, p.guestName,
        TYPE_FULL[p.travelerType] || p.travelerType, formatReportDate(p.checkIn), formatReportDate(p.checkOut),
        p.roomType || "", p.characteristic || "", p.board || "", p.supplierReference || "",
      ])
    );

  // Applied criteria (from the last Generate) + the active search, for the
  // exports so a generated report always shows what produced it.
  const activeFilterLines = () => {
    const a = applied || draft;
    const supName = options.suppliers.find((s) => String(s.id) === String(a.supplier))?.name || a.supplier;
    return buildFilterLines([
      ["Check-in from", a.dateFrom ? formatReportDate(a.dateFrom) : ""],
      ["Check-in to", a.dateTo ? formatReportDate(a.dateTo) : ""],
      ["Hotel", a.hotel],
      ["Country", a.country],
      ["Destination", a.destination],
      ["Room type", a.roomType],
      ["Characteristic", a.characteristic],
      ["Supplier", a.supplier ? supName : ""],
      ["Booking status", a.bookingStatus],
      ["Search", search],
    ]);
  };

  const handleExcel = () => {
    if (!filtered.length) return showToast("Nothing to export", "error");
    try {
      downloadExcel("hotel-report.xls", EXPORT_HEADERS, exportRows(), "Hotel Report — Rooming List", activeFilterLines());
      showToast("Excel exported", "success");
    } catch {
      showToast("Excel export failed", "error");
    }
  };

  const handlePdf = async () => {
    setPdfLoading(true);
    try {
      const blob = await downloadHotelReportPdf(applied || draft);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "hotel-report.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast("PDF exported", "success");
    } catch (err) {
      showToast(err?.response?.data?.message || "PDF export failed", "error");
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePrint = () => {
    if (!filtered.length) return showToast("Nothing to print", "error");
    const kpis = KPI_META.map(
      (k) => `<div class="kpi"><div class="l">${k.label}</div><div class="v">${summary[k.key] ?? 0}</div></div>`
    ).join("");
    const sections = filtered
      .map((h) => {
        const rows = h.roomingList
          .map(
            (p, i) =>
              `<tr><td>${i + 1}</td><td>${p.bookingReference}</td><td>${p.guestName}</td><td>${TYPE_FULL[p.travelerType] || p.travelerType}</td><td>${formatReportDate(p.checkIn)}</td><td>${formatReportDate(p.checkOut)}</td><td>${p.roomType || "—"}</td><td>${p.characteristic || "—"}</td><td>${p.board || "—"}</td><td>${p.supplierReference || "—"}</td></tr>`
          )
          .join("");
        const loc = [h.city, h.country].filter(Boolean).join(", ");
        return `<h3 style="margin:16px 0 6px">${h.hotelName}${loc ? " — " + loc : ""}${h.supplier ? " · " + h.supplier : ""}</h3>
          <table><thead><tr><th>#</th><th>Booking No</th><th>Guest</th><th>Type</th><th>Check-in</th><th>Check-out</th><th>Room Type</th><th>Characteristic</th><th>Board</th><th>Supplier Ref</th></tr></thead><tbody>${rows}</tbody></table>`;
      })
      .join("");
    const ok = printHtml(
      `<h1>Hotel Report</h1><p class="sub">Hotel rooming list · Generated ${new Date().toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })}</p>${filtersToPrintHtml(activeFilterLines())}<div class="kpis">${kpis}</div>${sections}`,
      "Hotel Report"
    );
    if (!ok) showToast("Please allow pop-ups to print", "error");
  };

  const pageNumbers = useMemo(() => {
    const nums = [];
    const cur = safePage;
    const win = 5;
    let s = Math.max(1, cur - Math.floor(win / 2));
    const e = Math.min(pageCount, s + win - 1);
    s = Math.max(1, e - win + 1);
    for (let i = s; i <= e; i++) nums.push(i);
    return nums;
  }, [pageCount, safePage]);

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div className={styles.headerIcon}><Hotel size={26} /></div>
        <div>
          <h1 className={styles.headerTitle}>Hotel Report</h1>
          <p className={styles.headerSub}>Create and export a hotel rooming list.</p>
        </div>
      </div>

      {/* Criteria */}
      <div className={styles.card}>
        <div className={styles.criteriaHead}><Filter size={16} /> Report Criteria</div>
        <div className={styles.criteriaGrid}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Check-in Date From</span>
            <input type="date" className={styles.input} value={draft.dateFrom} onChange={setField("dateFrom")} />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Check-in Date To</span>
            <input type="date" className={styles.input} value={draft.dateTo} onChange={setField("dateTo")} />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Hotel</span>
            <select className={styles.select} value={draft.hotel} onChange={setField("hotel")}>
              <option value="">All Hotels</option>
              {options.hotels.map((h) => <option key={h.name} value={h.name}>{h.name}</option>)}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Country</span>
            <select
              className={styles.select}
              value={draft.country}
              onChange={(e) => setDraft((d) => ({ ...d, country: e.target.value, destination: "" }))}
            >
              <option value="">All Countries</option>
              {options.countries.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
          </label>
          {/* Destination only appears once a country is picked, and lists just
              that country's destinations. */}
          {draft.country && (
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Destination</span>
              <select className={styles.select} value={draft.destination} onChange={setField("destination")}>
                <option value="">All Destinations</option>
                {(options.destinationsByCountry[draft.country] || []).map((city) => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </label>
          )}
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Room Type</span>
            <select className={styles.select} value={draft.roomType} onChange={setField("roomType")}>
              <option value="">All Room Types</option>
              {options.roomTypes.map((rt) => <option key={rt} value={rt}>{rt}</option>)}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Characteristic</span>
            <select className={styles.select} value={draft.characteristic} onChange={setField("characteristic")}>
              <option value="">All Characteristics</option>
              {options.characteristics.map((ch) => <option key={ch} value={ch}>{ch}</option>)}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Supplier</span>
            <select className={styles.select} value={draft.supplier} onChange={setField("supplier")}>
              <option value="">All Suppliers</option>
              {options.suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Booking Status</span>
            <select className={styles.select} value={draft.bookingStatus} onChange={setField("bookingStatus")}>
              <option value="">All</option>
              {BOOKING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <div className={styles.generateWrap}>
            <button className={styles.generateBtn} onClick={handleGenerate} disabled={loading} type="button">
              {loading ? <Loader2 size={16} className={styles.spin} /> : <Search size={16} />}
              Generate Report
            </button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className={styles.kpiGrid}>
        {KPI_META.map(({ key, label, Icon, cls }) => (
          <div key={key} className={`${styles.kpiCard} ${styles[cls]}`}>
            <span className={styles.kpiIcon}><Icon size={22} /></span>
            <div className={styles.kpiText}>
              <span className={styles.kpiLabel}>{label}</span>
              <span className={styles.kpiValue}>{loading ? "—" : (summary[key] ?? 0).toLocaleString("en-US")}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Results */}
      <div className={styles.card}>
        <div className={styles.resultsHead}>
          <div>
            <div className={styles.resultsTitle}><SlidersHorizontal size={16} /> Results</div>
            <div className={styles.resultsMeta}>
              {loading ? "Loading…" : `Showing ${rangeFrom} to ${rangeTo} of ${total} hotel${total === 1 ? "" : "s"}`}
            </div>
          </div>
          <div className={styles.searchBox}>
            <Search size={15} />
            <input
              className={styles.searchInput}
              placeholder="Search hotel, city, guest, supplier ref, room…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
        </div>

        {error ? (
          <div className={styles.errorBox}>{error}</div>
        ) : loading ? (
          <div className={styles.skeletonWrap}>{[...Array(4)].map((_, i) => <div key={i} className={styles.skeletonRow} />)}</div>
        ) : paged.length === 0 ? (
          <div className={styles.empty}>
            <Hotel size={30} />
            <div className={styles.emptyTitle}>No hotel stays found</div>
            <div className={styles.emptySub}>Adjust the criteria above and click <b>Generate Report</b>.</div>
          </div>
        ) : (
          <div className={styles.hotelList}>
            {paged.map((h) => (
              <HotelGroup key={h.key} hotel={h} open={expanded.has(h.key)} onToggle={() => toggleExpand(h.key)} />
            ))}
          </div>
        )}

        {!loading && total > 0 && (
          <div className={styles.pagination}>
            <div className={styles.rowsPer}>
              <span>Rows per page:</span>
              <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className={styles.rowsSelect}>
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className={styles.pager}>
              <button className={styles.pageBtn} disabled={safePage === 1} onClick={() => setPage(1)}>«</button>
              <button className={styles.pageBtn} disabled={safePage === 1} onClick={() => setPage(safePage - 1)}>‹</button>
              {pageNumbers.map((n) => (
                <button key={n} className={`${styles.pageBtn} ${n === safePage ? styles.pageBtnActive : ""}`} onClick={() => setPage(n)}>{n}</button>
              ))}
              <button className={styles.pageBtn} disabled={safePage === pageCount} onClick={() => setPage(safePage + 1)}>›</button>
              <button className={styles.pageBtn} disabled={safePage === pageCount} onClick={() => setPage(pageCount)}>»</button>
            </div>
            <div className={styles.showingText}>Showing {rangeFrom} to {rangeTo} of {total} hotels</div>
          </div>
        )}
      </div>

      {/* Export */}
      <div className={styles.exportCard}>
        <div className={styles.exportLeft}>
          <div className={styles.exportIcon}><FileText size={18} /></div>
          <div>
            <div className={styles.exportTitle}>Export Report</div>
            <div className={styles.exportSub}>Export the hotel rooming list in your preferred format.</div>
          </div>
        </div>
        <div className={styles.exportBtns}>
          <button className={`${styles.exportBtn} ${styles.excelBtn}`} onClick={handleExcel} disabled={loading} type="button">
            <FileSpreadsheet size={16} /> Export Excel
          </button>
          <button className={`${styles.exportBtn} ${styles.pdfBtn}`} onClick={handlePdf} disabled={loading || pdfLoading} type="button">
            {pdfLoading ? <Loader2 size={16} className={styles.spin} /> : <FileText size={16} />} Export PDF
          </button>
          <button className={`${styles.exportBtn} ${styles.printBtn}`} onClick={handlePrint} disabled={loading} type="button">
            <Printer size={16} /> Print
          </button>
        </div>
      </div>
    </div>
  );
};

export default HotelReport;
